import React, { useEffect, useState } from 'react'
import {
   makeStyles,
   Typography,
   Button,
   FormControl,
   FormHelperText,
   TextField,
   InputLabel,
   OutlinedInput,
   Grid,
   Chip,
   Select,
   MenuItem
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { useStateValue } from 'context/store'
import { getFromApi, postToApi } from 'actions'
import DragZoneUpload from '../../commons/DragZoneUpload'
import MessageView from './../UserProfilePopover/messageView'
import TooltipInputError from '../../toolTip/ToolTipInputError'
import {
   useApiRequest,
   handleErrorMessage
} from 'utils/customHooks/useApiRequest'

const addNewRegulationStyles = makeStyles(theme => ({
   addNewRegulation: {
      width: 310
   },
   formContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
   },
   header: {
      margin: theme.spacing(1, 0),
      display: 'flex'
   },
   title: {
      fontWeight: 'bold',
      margin: 'auto'
   },
   backButton: {
      cursor: 'pointer'
   },
   formControl: {
      width: '100%',
      fontSize: 14,
      margin: theme.spacing(1, 0)
   },
   button: {
      width: '100%',
      height: 40,
      marginTop: 20,
      color: 'white',
      background: '#3264DC'
   },
   helperText: {
      margin: theme.spacing(0),
      fontSize: 11,
      color: '#f44336'
   },
   dragzoneContainer: {
      margin: theme.spacing(1, 0)
   }
}))

const INITIAL_FORM_STATE = {
   subdomain: null,
   subject: '',
   comment: '',
   priority: null,
   file: null
}

function AddNewRegulation({ handleClose, setParentView }) {
   const classes = addNewRegulationStyles()
   const [currentView, setCurrentView] = useState('formView')
   const [formData, setForm] = useState(INITIAL_FORM_STATE)

   return (
      <Grid className={classes.addNewRegulation}>
         {currentView === 'formView' && (
            <FormView
               setCurrentView={setCurrentView}
               setParentView={setParentView}
               formData={formData}
               setForm={setForm}
            />
         )}
         {currentView === 'successMessageView' && (
            <MessageView
               handleClose={handleClose}
               title={'Circulares y Normativas'}
               successMessage={`Tu archivo ${formData.file.path} ha sido subido con éxito`}
            />
         )}
         {currentView === 'errorMessageView' && (
            <MessageView
               error={true}
               setCurrentView={setCurrentView}
               title={'Circulares y Normativas'}
            />
         )}
      </Grid>
   )
}

export default AddNewRegulation

// --------------------------------------

const INITIAL_FORM_VALIDATION = {
   subdomain: false,
   subject: false,
   comment: false,
   priority: false,
   file: false
}

const PRIORITIES = [
   { value: 'HIGH', label: 'Alta' },
   { value: 'MEDIUM', label: 'Media' },
   { value: 'LOW', label: 'Baja' }
]

function FormView({ setParentView, setCurrentView, formData, setForm }) {
   const classes = addNewRegulationStyles()

   const [subdomains, setSubdomains] = useState([])
   const [invalid, setInvalid] = useState(INITIAL_FORM_VALIDATION)

   const [, dispatch] = useStateValue()

   async function _getAllActiveSubdomains() {
      const url = `${process.env.REACT_APP_SUBDOMAIN_API}/subdomains/active`
      const searchedSubdomains = await getFromApi(url)
      setSubdomains(searchedSubdomains)
   }

   const getAllActiveSubdomains = useApiRequest(
      _getAllActiveSubdomains,
      'Nueva Normativa: Consultar Subdominios'
   )

   useEffect(() => {
      getAllActiveSubdomains()
   }, [])

   function goToRegulationsSummary() {
      setParentView('regulationsSummaryView')
   }

   function addValuesToMemory(e) {
      const { name, value } = e.target
      setForm(prev => ({
         ...prev,
         [name]: value
      }))
      setInvalid(prev => ({
         ...prev,
         [name]: !value
      }))
      return !value
   }

   function anyInvalidField() {
      let isInvalid = false
      Object.keys(formData).forEach(name => {
         const value = formData[name]
         const isCurrentFieldInvalid = addValuesToMemory({
            target: { name, value }
         })
         isInvalid = isInvalid || isCurrentFieldInvalid
      })
      return isInvalid
   }

   function onFileUpload(fileList) {
      setForm(prev => ({
         ...prev,
         file: fileList[0]
      }))
   }

   function onFileDelete() {
      setForm(prev => ({
         ...prev,
         file: null
      }))
   }

   async function registerNewRegulation() {
      dispatch({ type: 'OPEN_SPINNER' })
      dispatch({ type: 'CLOSE_SNACKBAR' })
      try {
         // NOTA: No aplica useApiRequest por callback en el catch
         const { file, subject, comment, subdomain, priority } = formData
         const { code } = subdomain
         const payload = new FormData()
         payload.append('file', file)
         payload.append('subject', subject)
         payload.append('comment', comment)
         payload.append('code', code)
         payload.append('priority', priority)
         const url = `${process.env.REACT_APP_NOTIFICATIONS_API}/circulars-regulations/receive`
         await postToApi(url, payload)
         setCurrentView('successMessageView')
      } catch (error) {
         dispatch({
            type: 'OPEN_SNACKBAR',
            openMessage: {
               message: `${handleErrorMessage(
                  error
               )} (Nueva Normativa: Registrar)`,
               severity: 'error'
            }
         })
         setCurrentView('errorMessageView')
      } finally {
         dispatch({ type: 'CLOSE_SPINNER' })
      }
   }

   function onSendRegulation() {
      if (!anyInvalidField()) {
         registerNewRegulation()
      }
   }

   return (
      <Grid className={classes.formContainer}>
         <Grid className={classes.header}>
            <ArrowBackIcon
               className={classes.backButton}
               onClick={goToRegulationsSummary}
            />
            <Typography className={classes.title}>
               Circulares y Normativas
            </Typography>
         </Grid>
         <Grid className={classes.form}>
            <FormControl
               size="small"
               variant="outlined"
               className={classes.formControl}
               error={invalid.subdomain}
            >
               <Autocomplete
                  size="small"
                  options={subdomains}
                  getOptionLabel={option => option.name}
                  renderOption={option => (
                     <span key={option.id}>{option.name}</span>
                  )}
                  value={formData.subdomain}
                  onChange={(e, newValue) => {
                     addValuesToMemory({
                        target: {
                           name: 'subdomain',
                           value: newValue || null
                        }
                     })
                  }}
                  renderInput={params => (
                     <TextField
                        {...params}
                        label={'Subdomino *'}
                        variant="outlined"
                        value={''}
                        error={invalid.subdomain}
                     />
                  )}
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.subdomain && 'El campo Subdominio es obligatorio'}
               </FormHelperText>
            </FormControl>

            <FormControl
               variant="outlined"
               size="small"
               className={classes.formControl}
               error={invalid.subject}
            >
               <InputLabel htmlFor="outlined-type-native-simple">
                  Asunto *
               </InputLabel>
               <OutlinedInput
                  label="Asunto"
                  name="subject"
                  size="small"
                  error={invalid.subject}
                  value={formData.subject}
                  onChange={addValuesToMemory}
                  inputProps={{ maxLength: 70 }}
                  endAdornment={
                     formData.subject.length === 70 && (
                        <TooltipInputError
                           title={
                              'El número permitido de caracteres es de máximo 70.'
                           }
                        />
                     )
                  }
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.subject && 'El campo Asunto es obligatorio'}
               </FormHelperText>
            </FormControl>

            <FormControl
               variant="outlined"
               size="small"
               className={classes.formControl}
               error={invalid.comment}
            >
               <InputLabel htmlFor="outlined-type-native-simple">
                  Comentario *
               </InputLabel>
               <OutlinedInput
                  label="Comentario"
                  name="comment"
                  size="small"
                  multiline
                  rows={3}
                  error={invalid.comment}
                  value={formData.comment}
                  onChange={addValuesToMemory}
                  inputProps={{ maxLength: 255 }}
                  endAdornment={
                     formData.comment.length === 255 && (
                        <TooltipInputError
                           title={
                              'El número permitido de caracteres es de máximo 255.'
                           }
                        />
                     )
                  }
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.comment && 'El campo Comentario es obligatorio'}
               </FormHelperText>
            </FormControl>

            <FormControl
               variant="outlined"
               className={classes.formControl}
               size="small"
               error={invalid.priority}
            >
               <InputLabel htmlFor="outlined-type-native-simple">
                  {'Prioridad *'}
               </InputLabel>
               <Select
                  value={formData.priority}
                  onChange={addValuesToMemory}
                  size="small"
                  name="priority"
                  label="Estado del Módulo  "
                  inputProps={{
                     name: 'priority',
                     id: 'outlined-type-native-simple'
                  }}
               >
                  <MenuItem aria-label="None" value={''} />
                  {PRIORITIES.map(option => (
                     <MenuItem key={option.value} value={option.value}>
                        {option.label}
                     </MenuItem>
                  ))}
               </Select>
               <FormHelperText className={classes.helperText}>
                  {invalid.priority && 'El campo Prioridad es obligatorio'}
               </FormHelperText>
            </FormControl>

            <Grid className={classes.dragzoneContainer}>
               {formData.file ? (
                  <Chip
                     size="small"
                     label={formData.file.path}
                     onDelete={onFileDelete}
                  />
               ) : (
                  <>
                     <DragZoneUpload
                        multiple={false}
                        maxFiles={1}
                        formats={'.pdf'}
                        showList={false}
                        fileListCallback={onFileUpload}
                        height={'170px'}
                     />
                     {invalid.file && (
                        <FormHelperText className={classes.helperText}>
                           {'Debe adjuntar un archivo'}
                        </FormHelperText>
                     )}
                  </>
               )}
            </Grid>

            <Button
               className={classes.button}
               type="submit"
               fullWidth
               variant="contained"
               color="primary"
               onClick={onSendRegulation}
            >
               Enviar
            </Button>
         </Grid>
      </Grid>
   )
}
